import { invalidate, useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const useMainCameraSyncHandler = () => {
    const syncMainCameras = usePlacementStore(useShallow((state) => state.syncMainCameras));

    const gl = useThree((state) => state.gl);
    const camera = useThree((state) => state.camera);
    const size = useThree((state) => state.size);
    const previousZoom = useRef<number | undefined>(undefined);
    const syncAnimationFrameRef = useRef<number | null>(null);
    const resizeRendererRef = useRef<boolean>(false);

    const scheduleMainCameraSync = useCallback(
        (resizeRenderer = false) => {
            resizeRendererRef.current ||= resizeRenderer;

            if (syncAnimationFrameRef.current !== null) {
                cancelAnimationFrame(syncAnimationFrameRef.current);
            }

            syncAnimationFrameRef.current = window.requestAnimationFrame(() => {
                syncAnimationFrameRef.current = null;

                if (resizeRendererRef.current) {
                    resizeRendererRef.current = false;
                    gl.setSize(size.width, size.height);
                }

                syncMainCameras(gl.domElement);
                invalidate();
            });
        },
        [gl, size.height, size.width, syncMainCameras]
    );

    // Sync cameras and handle canvas viewport on resize events
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            scheduleMainCameraSync(true);
        });

        resizeObserver.observe(gl.domElement);

        return () => {
            resizeObserver.disconnect();

            if (syncAnimationFrameRef.current !== null) {
                cancelAnimationFrame(syncAnimationFrameRef.current);
                syncAnimationFrameRef.current = null;
            }
        };
    }, [gl.domElement, scheduleMainCameraSync]);

    // Sync cameras on camera typeor zoom change
    useFrame(() => {
        if (previousZoom.current !== camera.zoom) {
            previousZoom.current = camera.zoom;
            scheduleMainCameraSync(false);
        }
    });
};
